import { Injectable, Injector } from "@angular/core";
import {
  BehaviorSubject,
  Observable,
  of,
  switchMap,
  take,
  catchError,
  EMPTY,
  filter,
  timeout,
} from "rxjs";
import { CommonService } from "../common/common.service";
import { FirestoreDataService } from "../firestore-data.service";

/**
 * App-layer SW helpers only. Must not depend on AuthService (Derick): use
 * CommonService + FirestoreDataService so SW ownership stays outside auth DI.
 */
@Injectable({
  providedIn: "root",
})
export class SWServiceService {
  private firstFetchSubject = new BehaviorSubject<boolean>(true);

  constructor(private injector: Injector) {
    // Defer construction-time work so consumers can finish wiring first.
    queueMicrotask(() => this.updateUserDoc());
  }

  checkLegacyServiceWorkerRegistration(): Observable<boolean> {
    return new Observable((observer) => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker
          ?.getRegistrations()
          ?.then((registrations) => {
            let found = false;
            for (const registration of registrations) {
              if (
                registration.active &&
                registration.active.scriptURL.includes("ngsw-worker.js")
              ) {
                observer.next(true);
                observer.complete();
                found = true;
                break;
              } else if (
                registration.active &&
                registration.active.scriptURL.includes("service-worker.js")
              ) {
                observer.next(false);
                observer.complete();
                found = true;
                break;
              }
            }
            if (!found)
              observer.error("No matching service worker registration found.");
          })
          .catch((error) => observer.error(error));
      } else {
        observer.error("Service workers are not supported in this browser.");
      }
    });
  }

  unRegisterServiceWorker(): void {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker?.getRegistrations()?.then((registrations) => {
        for (let registration of registrations) {
          registration.unregister().catch((err) => {
            console.error("Error during service worker un-registration:", err);
          });
        }
      });
    }
  }

  updateUserDoc(): void {
    this.firstFetchSubject
      .pipe(
        take(1),
        switchMap((firstFetch) => {
          if (!firstFetch) {
            return EMPTY;
          }

          const commonService = this.injector.get(CommonService);
          const firestoreDataService = this.injector.get(FirestoreDataService);

          return commonService.getUid("SWServiceService").pipe(
            filter((userId): userId is string => !!userId && userId.length > 6),
            take(1),
            timeout({ each: 15000, with: () => EMPTY }),
            switchMap(() => {
              return firestoreDataService
                .getV2CloudFunction("updatefirestoredoc", {
                  path_id: "yzF1Ss5eiMqnh91zQMtv",
                  doc_data: {
                    sw_hash: localStorage.getItem("sw_hash"),
                    swUpdatedAt: new Date(),
                  },
                  method: "update",
                })
                .pipe(
                  catchError((error) => {
                    console.error("Error updating user document:", error);
                    return of(null);
                  })
                );
            }),
            catchError((error) => {
              console.error("Error getting UID:", error);
              return EMPTY;
            })
          );
        })
      )
      .subscribe({
        next: () => {
          this.firstFetchSubject.next(false);
        },
        error: (error) => {
          console.error("Error in updateUserDoc:", error);
        },
      });
  }
}
